import type { VaultApy, VaultApyRange } from '@/types/apy'
import type { StepName } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import { calculateReturns } from '@/utils/math/calculateReturns'
import { DECIMAL_SCALE, getTokenNumberValue } from '@vaultsfyi/common/utils'
import type { HTMLAttributes } from 'react'
import { AERA_PENDING_TAB_NAME } from './actionTab/aeraPendingTab'
import { BORING_REDEEM_TAB_NAME } from './actionTab/boringWaitForRedeemTab'
import { CLAIM_REWARDS_EXCEPTION_TAB_NAME } from './actionTab/claimRewardsExceptionTab'
import { GENERIC_PENDING_TAB_NAME } from './actionTab/genericPendingTab'
import { LIDO_WRAP_TAB_NAME } from './actionTab/lidoWrappingTab'
import { UMBRELLA_WITHDRAW_TAB_NAME } from './actionTab/umbrellaWithdrawTab'
import { YO_WAIT_FOR_REDEEM_TAB_NAME } from './actionTab/yoWaitForRedeemTab'

const TIMESPANS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 365, label: '1 year' },
] as const

interface EstimatedReturnsWidgetProps {
  token: Vault['asset']
  parsedActionAmount: bigint
  positionValue: bigint
  baseApy: VaultApy[VaultApyRange]['base']
  containRewards?: boolean
  tabValue: StepName
  className?: string
}

export function EstimatedReturnsWidget({ className, ...rowProps }: EstimatedReturnsWidgetProps) {
  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {TIMESPANS.map(({ days, label }) => (
        <EstimatedReturnsCard key={days} {...rowProps} timespan={days} label={label} />
      ))}
    </div>
  )
}

type EstimatedReturnsCardProps = EstimatedReturnsWidgetProps & {
  timespan: number
  label: string
} & HTMLAttributes<HTMLDivElement>

function EstimatedReturnsCard({
  token,
  parsedActionAmount,
  positionValue,
  baseApy,
  containRewards,
  timespan,
  label,
  tabValue,
}: EstimatedReturnsCardProps) {
  const returnsBeforeAction = calculateReturns({ positionValue, apy: baseApy, timespan })
  const returnsChange = calculateReturns({
    positionValue: BigInt(parsedActionAmount),
    apy: baseApy,
    timespan,
  })
  const valueChanged = returnsChange !== 0n

  const { colorClass, newReturns } = getWidgetConfig({
    tabValue,
    returnsBeforeAction,
    returnsChange,
  })

  const isPositive = newReturns > 0n
  const isNegative = newReturns < 0n
  const effectiveColorClass = !valueChanged
    ? 'text-success'
    : isNegative
      ? 'text-destructive'
      : colorClass || 'text-success'

  const percentage = Math.floor((Math.round(baseApy * DECIMAL_SCALE) * timespan) / 365) / 100
  const tokenAmount = getTokenNumberValue({ value: newReturns, decimals: token.decimals })
  const absTokenAmount = Math.abs(tokenAmount)
  const tokenDisplay =
    absTokenAmount < 0.01
      ? '<0.01'
      : new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(absTokenAmount)

  const amountSign = isPositive ? '+' : isNegative ? '-' : ''
  const percentageSign = isPositive ? '+' : isNegative ? '-' : ''

  return (
    <div className="flex flex-1 flex-col gap-1 rounded-2xl border bg-page p-3">
      <span className="text-sm">{label}</span>
      {newReturns === 0n ? (
        <p className="font-medium text-heading-foreground text-sm">––</p>
      ) : (
        <>
          <p className="whitespace-nowrap font-medium text-heading-foreground text-sm">
            {amountSign}
            {tokenDisplay} <span>{token.symbol}</span>
          </p>
          <span className={cn('font-medium text-xs', effectiveColorClass)}>
            {percentageSign}
            {Math.abs(percentage).toFixed(2)}%
          </span>
        </>
      )}
      {valueChanged && containRewards && <span className="text-xs">+ rewards</span>}
    </div>
  )
}

function getWidgetConfig({
  tabValue,
  returnsBeforeAction,
  returnsChange,
}: {
  tabValue: StepName
  returnsBeforeAction: bigint
  returnsChange: bigint
}) {
  switch (tabValue) {
    case 'deposit':
    case 'request-deposit':
    case 'claim-deposit':
      return {
        colorClass: 'text-success',
        newReturns: returnsBeforeAction + returnsChange,
      } as const
    case 'redeem':
    case 'request-redeem':
    case 'claim-redeem':
    case UMBRELLA_WITHDRAW_TAB_NAME: {
      const absChange = returnsChange < 0n ? -returnsChange : returnsChange
      const absBefore = returnsBeforeAction < 0n ? -returnsBeforeAction : returnsBeforeAction
      const effectiveChange = absChange > absBefore ? returnsBeforeAction : returnsChange
      const newReturns = returnsBeforeAction - effectiveChange

      return {
        colorClass: 'text-warning',
        newReturns,
      } as const
    }
    case 'claim-rewards':
    case CLAIM_REWARDS_EXCEPTION_TAB_NAME:
    case LIDO_WRAP_TAB_NAME:
    case BORING_REDEEM_TAB_NAME:
    case YO_WAIT_FOR_REDEEM_TAB_NAME:
    case AERA_PENDING_TAB_NAME:
    case GENERIC_PENDING_TAB_NAME: {
      return {
        colorClass: '',
        newReturns: returnsBeforeAction,
      }
    }
    default:
      throw new Error(`Unknown tab value: ${tabValue}`)
  }
}
